import { FieldDefinition } from '@servicetitan/financing-workflow-shared';
import {
    DateField,
    PhoneInput,
    TextField,
    SSNInput,
    RadioGroup,
    Radio,
} from '@servicetitan/anvil2-react-hook-form';
import { IncomeTypeCombobox } from '../common/IncomeTypeCombobox';
import {
    Translate,
    maskSsn,
    formatCurrency,
    incomeTypes,
    isValidDate,
    isOldEnough,
} from '@servicetitan/financing-utils-shared';
import {
    Citizenship,
    EmploymentStatus,
    ResidenceType,
    SpokenLanguage,
} from '@servicetitan/financing-api-client';
import type { ProviderConfig } from '@servicetitan/financing-workflow-shared/src/features/providers/configs';
import { z } from 'zod';

// Helper function to create date field with maxDate
const createDateField = (prefix: string) => {
    const maxDate = new Date().toISOString().split('T')[0];
    return (
        <DateField
            name={[prefix, 'dateOfBirth'].join('.')}
            label="Date of Birth"
            minDate="1920-01-01"
            maxDate={maxDate}
            required
        />
    );
};

// Helper function to create monthly income field
const createMonthlyIncomeField = (prefix: string) => {
    return (
        <TextField
            name={[prefix, 'employment.grossMonthlyIncome'].join('.')}
            label="Gross Monthly Income"
            prefix="$"
            type="number"
            inputMode="decimal"
            required
        />
    );
};

// Helper function to create annual income field
const createAnnualIncomeField = (prefix: string) => {
    return (
        <TextField
            name={[prefix, 'annualIncome'].join('.')}
            label="Gross Annual Income"
            prefix="$"
            type="number"
            inputMode="decimal"
            required
        />
    );
};

// Personal Information Field Registry
export const PERSONAL_INFO_FIELD_REGISTRY: Record<string, FieldDefinition> = {
    requestedLoanAmount: {
        id: 'requestedLoanAmount',
        component: (prefix: string) => (
            <TextField
                name={[prefix, 'requestedLoanAmount'].join('.')}
                label="Requested Loan Amount"
                prefix="$"
                type="number"
                inputMode="decimal"
                required
            />
        ),
        schema: z
            .number({
                // eslint-disable-next-line @typescript-eslint/naming-convention
                required_error: 'Enter your requested loan amount',
                // eslint-disable-next-line @typescript-eslint/naming-convention
                invalid_type_error: 'Enter a valid number',
            })
            .gt(0, { message: 'Must be greater than $0' }),
        reviewLabel: 'Requested Loan Amount',
        reviewValue: applicant => {
            const value = applicant?.requestedLoanAmount;
            return value ? formatCurrency(value) : null;
        },
    },

    firstName: {
        id: 'firstName',
        component: (prefix: string) => (
            <TextField name={[prefix, 'firstName'].join('.')} label="First Name" required />
        ),
        schema: z
            // eslint-disable-next-line @typescript-eslint/naming-convention
            .string({ required_error: 'Enter your first name' })
            .trim()
            .min(1, { message: 'Enter your first name' })
            .max(50, { message: 'First name must be 50 characters or less' })
            .regex(/^[a-zA-Z-' ]+$/, {
                message: 'First name contains characters that are not allowed',
            }),
        reviewLabel: 'First Name',
        reviewValue: applicant => applicant?.firstName,
    },

    middleInitial: {
        id: 'middleInitial',
        component: (prefix: string) => (
            <TextField name={[prefix, 'middleInitial'].join('.')} label="Middle Initial" />
        ),
        schema: z.string().max(1).optional(),
        reviewLabel: 'Middle Initial',
        reviewValue: applicant => applicant?.middleInitial ?? null,
    },

    lastName: {
        id: 'lastName',
        component: (prefix: string) => (
            <TextField name={[prefix, 'lastName'].join('.')} label="Last Name" required />
        ),
        schema: z
            // eslint-disable-next-line @typescript-eslint/naming-convention
            .string({ required_error: 'Enter your last name' })
            .trim()
            .min(1, { message: 'Enter your last name' }),
        reviewLabel: 'Last Name',
        reviewValue: applicant => applicant?.lastName,
    },

    email: {
        id: 'email',
        component: (prefix: string) => (
            <TextField
                name={[prefix, 'email'].join('.')}
                label="Email Address"
                inputMode="email"
                required
            />
        ),
        schema: z
            // eslint-disable-next-line @typescript-eslint/naming-convention
            .string({ required_error: 'Enter your email address' })
            .email({ message: 'Does not match format: example@email.com' }),
        reviewLabel: 'Email Address',
        reviewValue: applicant => applicant?.email,
        getDescription: (config, disclosures) => {
            if (config.emailDisclosureCategory) {
                return disclosures.email ?? undefined;
            }
            if (
                config.disclosureBlocks?.includes('serviceFinance') ||
                config.disclosureBlocks?.includes('turns')
            ) {
                return 'By providing your e-mail address, you agree that your Home Improvement Dealer, its financing partners, or other service providers may use it to contact you about your account and tell you about useful products and services.';
            }
            return undefined;
        },
    },

    primaryPhone: {
        id: 'primaryPhone',
        component: (prefix: string) => (
            <PhoneInput
                name={[prefix, 'primaryPhone'].join('.')}
                label="Primary Phone Number"
                required
            />
        ),
        schema: z
            // eslint-disable-next-line @typescript-eslint/naming-convention
            .string({ required_error: 'Does not match phone format: (xxx) xxx-xxxx' })
            .regex(/^((\d{3}\-|\(\d{3}\)\s)\d{3}-\d{4}|\d{10})$/, {
                message: 'Does not match phone format: (xxx) xxx-xxxx',
            }),
        reviewLabel: 'Primary Phone Number',
        reviewValue: applicant => applicant?.primaryPhone,
        getDescription: (config, disclosures) => {
            if (config.phoneDisclosureCategory) {
                return disclosures.phone ?? undefined;
            }
            if (
                config.disclosureBlocks?.includes('serviceFinance') ||
                config.disclosureBlocks?.includes('turns')
            ) {
                return 'By providing your phone number, you agree that your Home Improvement Dealer, financing partners including Service Finance or its service providers may contact you at this number by text message, artificial voice, pre-recorded or autodialed calls. Your phone plan charges may apply.';
            }
            return undefined;
        },
    },

    secondaryPhone: {
        id: 'secondaryPhone',
        component: (prefix: string) => (
            <PhoneInput
                name={[prefix, 'secondaryPhone'].join('.')}
                label="Secondary Phone Number"
            />
        ),
        schema: z
            .string()
            .refine(
                value =>
                    value === '(___) ___-____' ||
                    /^((\d{3}\-|\(\d{3}\)\s)\d{3}-\d{4}|\d{10})$/.test(value),

                {
                    message: 'Does not match phone format: (xxx) xxx-xxxx',
                },
            )
            .optional()
            .nullable(),
        reviewLabel: 'Secondary Phone Number',
        reviewValue: applicant => applicant?.secondaryPhone,
    },

    dateOfBirth: {
        id: 'dateOfBirth',
        component: createDateField,
        schema: z
            // eslint-disable-next-line @typescript-eslint/naming-convention
            .string({ required_error: 'Enter your date of birth' })
            .regex(/^\d{2}\/\d{2}\/\d{4}$/, { message: 'Does not match format: mm/dd/yyyy' })
            .refine(
                dob => {
                    return isValidDate(dob);
                },
                { message: 'Enter a valid date' },
            )
            .refine(
                dob => {
                    // Parse mm/dd/yyyy format
                    const [month, day, year] = dob.split('/').map(Number);
                    const date = new Date(year, month - 1, day);
                    const minDate = new Date(1920, 0, 1);
                    return date >= minDate;
                },
                { message: 'Date of birth must be on or after 01/01/1920' },
            )
            .refine(
                dob => {
                    // Parse mm/dd/yyyy format
                    const [month, day, year] = dob.split('/').map(Number);
                    const date = new Date(year, month - 1, day);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date <= today;
                },
                { message: 'Date of birth cannot be in the future' },
            )
            .refine(
                dob => {
                    return isOldEnough(dob);
                },
                { message: 'Must be 18 years or older' },
            ),
        reviewLabel: 'Date Of Birth',
        reviewValue: applicant => applicant?.dateOfBirth,
        getDescription: config => {
            if (config.disclosureBlocks?.includes('greenSky')) {
                return 'Only U.S. Residents 18 years and older may apply';
            }
            return undefined;
        },
    },

    ssn: {
        id: 'ssn',
        component: (prefix: string) => (
            <SSNInput name={[prefix, 'ssn'].join('.')} label="Social Security Number" required />
        ),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        schema: z.string({ required_error: 'Enter your SSN' }).refine(
            value => {
                return (
                    value.replace(/-/g, '').length === 9 &&
                    (/^\d{3}-\d{2}-\d{4}$/.test(value) || /\d{9}/.test(value))
                );
            },
            { message: 'Does not match SSN format: xxx-xx-xxxx' },
        ),
        reviewLabel: 'Social Security Number',
        reviewValue: applicant => {
            const ssn = applicant?.ssn;
            if (!ssn) {
                return null;
            }
            return maskSsn(ssn);
        },
    },

    employerName: {
        id: 'employerName',
        component: (prefix: string) => (
            <TextField
                name={[prefix, 'employment.employerName'].join('.')}
                label="Employer Name"
                required
            />
        ),
        schema: z
            // eslint-disable-next-line @typescript-eslint/naming-convention
            .string({ required_error: 'Enter your employer name' })
            .trim()
            .min(1, { message: 'Enter your employer name' }),
        reviewLabel: 'Employer Name',
        reviewValue: applicant => applicant?.employment?.employerName ?? null,
    },

    grossMonthlyIncome: {
        id: 'grossMonthlyIncome',
        component: (prefix: string) => createMonthlyIncomeField(prefix),
        schema: (config?: ProviderConfig) => {
            const providerCategory = config?.providerCategory;
            return z
                .number({
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    required_error: 'Enter your gross monthly income',
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    invalid_type_error: 'Enter a valid number',
                })
                .gt(0, { message: 'Must be greater than $0' })
                .refine(
                    value => {
                        if (providerCategory === 'second-look') {
                            return value <= 30000;
                        }
                        return true;
                    },
                    { message: 'Income must not exceed $30,000' },
                );
        },
        reviewLabel: 'Gross Monthly Income',
        reviewValue: applicant => {
            const value = applicant?.employment?.grossMonthlyIncome;
            return value ? formatCurrency(value) : null;
        },
        getDescription: config => {
            if (config.disclosureBlocks?.includes('greenSky')) {
                return 'List your total available wages, retirement benefits and any income from investments or rental properties. Alimony, child support or separate maintenance is optional. If you include non-taxable income/benefits increase them by 25%';
            }
            if (
                config.disclosureBlocks?.includes('serviceFinance') ||
                config.disclosureBlocks?.includes('turns')
            ) {
                return 'Alimony, child support, or separate maintenance income need not be revealed if you do not wish to have it considered as a basis for repaying this obligation.';
            }
            return undefined;
        },
    },

    annualIncome: {
        id: 'annualIncome',
        component: (prefix: string) => createAnnualIncomeField(prefix),
        schema: (config?: ProviderConfig) => {
            const providerCategory = config?.providerCategory;
            return z
                .number({
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    required_error: 'Enter your gross annual income',
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    invalid_type_error: 'Enter a valid number',
                })
                .gt(0, { message: 'Must be greater than $0' })
                .refine(
                    value => {
                        if (providerCategory === 'second-look') {
                            return value <= 360000;
                        }
                        return true;
                    },
                    { message: 'Income must not exceed $360,000' },
                );
        },
        reviewLabel: 'Gross Annual Income',
        reviewValue: applicant => {
            const value = applicant?.annualIncome;
            return value ? formatCurrency(value) : null;
        },
        getDescription: config => {
            if (config.disclosureBlocks?.includes('greenSky')) {
                return 'List your total available wages, retirement benefits and any income from investments or rental properties. Alimony, child support or separate maintenance is optional. If you include non-taxable income/benefits increase them by 25%';
            }
            if (
                config.disclosureBlocks?.includes('serviceFinance') ||
                config.disclosureBlocks?.includes('turns')
            ) {
                return 'Alimony, child support, or separate maintenance income need not be revealed if you do not wish to have it considered as a basis for repaying this obligation.';
            }
            return undefined;
        },
    },

    pretaxIncome: {
        id: 'pretaxIncome',
        component: (prefix: string) => (
            <TextField
                name={[prefix, 'annualIncome'].join('.')}
                label="Individual Pre-tax Income"
                prefix="$"
                type="number"
                inputMode="decimal"
                required
            />
        ),
        // pretaxIncome behaves like annualIncome but with a different label
        schemaKey: 'annualIncome',
        schema: (config?: ProviderConfig) => {
            const providerCategory = config?.providerCategory;
            return z
                .number({
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    required_error: 'Enter your pre-tax income',
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    invalid_type_error: 'Enter a valid number',
                })
                .gt(0, { message: 'Must be greater than $0' })
                .refine(
                    value => {
                        if (providerCategory === 'second-look') {
                            return value <= 360000;
                        }
                        return true;
                    },
                    { message: 'Income must not exceed $360,000' },
                );
        },
        reviewLabel: 'Individual Pre-tax Income',
        reviewValue: applicant => {
            const value = applicant?.annualIncome;
            return value ? formatCurrency(value) : null;
        },
    },

    incomeType: {
        id: 'incomeType',
        component: (prefix: string) => <IncomeTypeCombobox fieldPrefix={prefix} />,
        schema: z
            // eslint-disable-next-line @typescript-eslint/naming-convention
            .string({ required_error: 'Select an income type' })
            .refine(value => Object.values(incomeTypes).includes(value), {
                message: 'Please select a valid income type',
            }),
        reviewLabel: 'Income Type',
        reviewValue: applicant => applicant?.employment?.incomeType ?? null,
    },

    typeOfHome: {
        id: 'typeOfHome',
        component: (prefix: string) => (
            <RadioGroup
                name={[prefix, 'typeOfHome'].join('.')}
                legend="Type of Home"
                gridColumn="1/1"
                required
            >
                <Radio
                    name={[prefix, 'typeOfHome'].join('.')}
                    value={ResidenceType.Primary}
                    label={Translate.residenceType(ResidenceType.Primary)}
                />
                <Radio
                    name={[prefix, 'typeOfHome'].join('.')}
                    value={ResidenceType.Secondary}
                    label={Translate.residenceType(ResidenceType.Secondary)}
                />
                <Radio
                    name={[prefix, 'typeOfHome'].join('.')}
                    value={ResidenceType.Investment}
                    label={Translate.residenceType(ResidenceType.Investment)}
                />
            </RadioGroup>
        ),
        schema: z.nativeEnum(ResidenceType, {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            required_error: 'Select a type of home',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            invalid_type_error: 'Select a valid type of home',
        }),
        reviewLabel: 'Type of Home',
        reviewValue: applicant =>
            applicant?.typeOfHome ? Translate.residenceType(applicant.typeOfHome) : null,
    },

    employmentStatus: {
        id: 'employmentStatus',
        component: (prefix: string) => (
            <RadioGroup
                name={[prefix, 'employment.status'].join('.')}
                legend="Employment Status"
                style={{ width: '100%' }}
                gridColumn="1/1"
                required
            >
                <Radio
                    name={[prefix, 'employment.status'].join('.')}
                    value={EmploymentStatus.Employed}
                    label={Translate.employmentStatus(EmploymentStatus.Employed)}
                />
                <Radio
                    name={[prefix, 'employment.status'].join('.')}
                    value={EmploymentStatus.NotEmployed}
                    label={Translate.employmentStatus(EmploymentStatus.NotEmployed)}
                />
                <Radio
                    name={[prefix, 'employment.status'].join('.')}
                    value={EmploymentStatus.Retired}
                    label={Translate.employmentStatus(EmploymentStatus.Retired)}
                />
                <Radio
                    name={[prefix, 'employment.status'].join('.')}
                    value={EmploymentStatus.Other}
                    label={Translate.employmentStatus(EmploymentStatus.Other)}
                />
            </RadioGroup>
        ),
        schema: z.nativeEnum(EmploymentStatus, {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            required_error: 'Select an employment status',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            invalid_type_error: 'Select a valid employment status',
        }),
        reviewLabel: 'Employment Status',
        reviewValue: applicant =>
            applicant?.employment?.status
                ? Translate.employmentStatus(applicant.employment.status)
                : null,
    },

    citizenshipStatus: {
        id: 'citizenship',
        component: (prefix: string) => (
            <RadioGroup
                name={[prefix, 'citizenship'].join('.')}
                legend="Citizenship Status"
                style={{ width: '100%' }}
                gridColumn="1/1"
                required
            >
                <Radio
                    name={[prefix, 'citizenship'].join('.')}
                    value={Citizenship.USCitizen}
                    label={Translate.citizenship(Citizenship.USCitizen)}
                />
                <Radio
                    name={[prefix, 'citizenship'].join('.')}
                    value={Citizenship.LawfulPermanentResident}
                    label={Translate.citizenship(Citizenship.LawfulPermanentResident)}
                />
                <Radio
                    name={[prefix, 'citizenship'].join('.')}
                    value={Citizenship.Other}
                    label={Translate.citizenship(Citizenship.Other)}
                />
            </RadioGroup>
        ),
        schemaKey: 'citizenship',
        schema: z.nativeEnum(Citizenship, {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            required_error: 'Select a citizenship status',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            invalid_type_error: 'Select a valid citizenship status',
        }),
        reviewLabel: 'Citizenship',
        reviewValue: applicant =>
            applicant?.citizenship ? Translate.citizenship(applicant.citizenship) : null,
    },

    isUSCitizenField: {
        id: 'isUSCitizen',
        component: (prefix: string) => (
            <RadioGroup
                name={[prefix, 'isUSCitizen'].join('.')}
                legend="Citizenship Status"
                style={{ width: '100%' }}
                gridColumn="1/1"
                required
            >
                <Radio name={[prefix, 'isUSCitizen'].join('.')} value="true" label="U.S. citizen" />
                <Radio name={[prefix, 'isUSCitizen'].join('.')} value="false" label="Other" />
            </RadioGroup>
        ),
        schemaKey: 'isUSCitizen',
        schema: z
            .enum(['true', 'false'], {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                required_error: 'Select a citizenship status',
            })
            .transform(val => val === 'true'),
        reviewLabel: 'Citizenship',
        reviewValue: applicant => (applicant?.isUSCitizen === 'true' ? 'U.S. citizen' : 'Other'),
    },

    spokenLanguage: {
        id: 'spokenLanguage',
        component: (prefix: string) => (
            <RadioGroup
                name={[prefix, 'spokenLanguage'].join('.')}
                legend="Preferred Language"
                gridColumn="1/1"
                required
            >
                <Radio
                    name={[prefix, 'spokenLanguage'].join('.')}
                    value={SpokenLanguage.English}
                    label={Translate.spokenLanguage(SpokenLanguage.English)}
                />
                <Radio
                    name={[prefix, 'spokenLanguage'].join('.')}
                    value={SpokenLanguage.Spanish}
                    label={Translate.spokenLanguage(SpokenLanguage.Spanish)}
                />
            </RadioGroup>
        ),
        schema: z.nativeEnum(SpokenLanguage, {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            required_error: 'Select a preferred language',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            invalid_type_error: 'Please select a valid language option',
        }),
        reviewLabel: 'Preferred Language',
        reviewValue: applicant =>
            applicant?.spokenLanguage ? Translate.spokenLanguage(applicant.spokenLanguage) : null,
    },
};
